import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const ZoomContext = createContext();

export const useZoom = () => {
  const context = useContext(ZoomContext);
  if (!context) {
    throw new Error('useZoom must be used within a ZoomProvider');
  }
  return context;
};

const ZOOM_LEVELS = [80, 90, 100, 110, 120];
const DEFAULT_ZOOM = 100;

export const ZoomProvider = ({ children }) => {
  const [zoomLevel, setZoomLevel] = useState(() => {
    const saved = localStorage.getItem('velaris_zoom');
    return saved ? parseInt(saved, 10) : DEFAULT_ZOOM;
  });

  useEffect(() => {
    document.documentElement.style.fontSize = `${zoomLevel}%`;
    localStorage.setItem('velaris_zoom', zoomLevel.toString());
  }, [zoomLevel]);

  const zoomIn = useCallback(() => {
    setZoomLevel(prev => {
      const currentIndex = ZOOM_LEVELS.indexOf(prev);
      if (currentIndex < ZOOM_LEVELS.length - 1) {
        return ZOOM_LEVELS[currentIndex + 1];
      }
      return prev;
    });
  }, []);

  const zoomOut = useCallback(() => {
    setZoomLevel(prev => {
      const currentIndex = ZOOM_LEVELS.indexOf(prev);
      if (currentIndex > 0) {
        return ZOOM_LEVELS[currentIndex - 1];
      }
      return prev;
    });
  }, []);

  const resetZoom = useCallback(() => {
    setZoomLevel(DEFAULT_ZOOM);
  }, []);

  const canZoomIn = ZOOM_LEVELS.indexOf(zoomLevel) < ZOOM_LEVELS.length - 1;
  const canZoomOut = ZOOM_LEVELS.indexOf(zoomLevel) > 0;

  const value = {
    zoomLevel,
    zoomIn,
    zoomOut,
    resetZoom,
    canZoomIn,
    canZoomOut,
  };

  return (
    <ZoomContext.Provider value={value}>
      {children}
    </ZoomContext.Provider>
  );
};

export default ZoomContext;
